import { ReactNode, useState, useSyncExternalStore } from 'react'
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'

/**
 * Design-notes layer for the wireframes.
 *
 * Notes are authored in-code, co-located with each screen, so the proposal can
 * explain itself: what a screen replaces, where it's reached from, what's a
 * deliberate stub, and the open questions. They come in two grains —
 * `PageDesignNote` (the whole screen's role in the IA) and `DesignNote` (an
 * inline pin on a specific control) — and the whole layer can be toggled off
 * for clean screenshots.
 *
 * The annotation pins reuse the "arwen dot" (the brand's red full stop) so the
 * layer reads as a deliberate Arwen design artifact, not bolted-on tooling.
 */

// Visibility is shared between the banner toggle and every note on the page but
// they aren't in a parent/child relationship, so we use a tiny external store
// (persisted to localStorage) rather than threading context through every page
// or adding a wireframe-only atom to the global Recoil store.
const STORAGE_KEY = 'arwen.wireframes.designNotesVisible'
const listeners = new Set<() => void>()
let visibleState = readInitial()

function readInitial(): boolean {
    try {
        // Default ON — the notes are the point; you opt out for clean shots.
        return window.localStorage.getItem(STORAGE_KEY) !== 'false'
    } catch {
        return true
    }
}

function setDesignNotesVisible(next: boolean) {
    visibleState = next
    try {
        window.localStorage.setItem(STORAGE_KEY, String(next))
    } catch {
        // localStorage can throw in private-mode/sandboxed contexts — the toggle
        // still works for the session, it just won't persist.
    }
    listeners.forEach((l) => l())
}

function subscribe(cb: () => void) {
    listeners.add(cb)
    return () => listeners.delete(cb)
}

export function useDesignNotesVisible() {
    const visible = useSyncExternalStore(
        subscribe,
        () => visibleState,
        () => true
    )
    return [visible, setDesignNotesVisible] as const
}

// ---------------------------------------------------------------------------
// Note types — rendered as chips so a reviewer knows what kind of thing they're
// reading at a glance. Neutral palette except `new`/`replaces`, the two that
// describe how the proposal maps onto the live app (the reason these wireframes
// sit inside real flows at all).
// ---------------------------------------------------------------------------

export type NoteType = 'new' | 'replaces' | 'rationale' | 'question' | 'gap' | 'stub'

const NOTE_META: Record<NoteType, { label: string; chip: string }> = {
    new: { label: 'New', chip: 'bg-arwen-600 text-white' },
    replaces: { label: 'Replaces', chip: 'bg-arwen-50 text-arwen-700 ring-1 ring-inset ring-arwen-200' },
    rationale: { label: 'Rationale', chip: 'bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-200' },
    question: { label: 'Open question', chip: 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200' },
    gap: { label: 'Known gap', chip: 'bg-orange-50 text-orange-800 ring-1 ring-inset ring-orange-200' },
    stub: { label: 'Not built yet', chip: 'bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-300' }
}

function NoteTypeChip({ type }: { type: NoteType }) {
    const meta = NOTE_META[type]
    return (
        <span
            className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${meta.chip}`}
        >
            {meta.label}
        </span>
    )
}

// ---------------------------------------------------------------------------
// Banner toggle — lives in WireframeBanner, controls the whole layer.
// ---------------------------------------------------------------------------

export function DesignNotesToggle() {
    const [visible, setVisible] = useDesignNotesVisible()
    return (
        <button
            type="button"
            onClick={() => setVisible(!visible)}
            className="ml-auto inline-flex flex-none items-center gap-1.5 rounded border border-white/40 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-white/10"
        >
            {visible ? (
                <EyeSlashIcon className="h-3.5 w-3.5" />
            ) : (
                <EyeIcon className="h-3.5 w-3.5" />
            )}
            {visible ? 'Hide design notes' : 'Show design notes'}
        </button>
    )
}

// ---------------------------------------------------------------------------
// Page-level note — the screen's role in the IA. Sits under the wireframe
// banner, above the masthead.
// ---------------------------------------------------------------------------

interface PageDesignNoteProps {
    type?: NoteType
    /** The existing page/feature this screen would replace, if any. */
    replaces?: string
    /** Where this screen would also be reachable from in the live app, if any. */
    reachableFrom?: string
    children: ReactNode
}

export function PageDesignNote({
    type = 'rationale',
    replaces,
    reachableFrom,
    children
}: PageDesignNoteProps) {
    const [visible] = useDesignNotesVisible()
    if (!visible) return null

    // When a `replaces` target is given, lead with the relationship chip even if
    // the caller didn't set type — that mapping is the headline.
    const chipType: NoteType = replaces ? 'replaces' : type

    return (
        <aside className="flex items-start gap-3 border-b border-arwen-100 bg-arwen-50 px-6 py-3 text-sm text-gray-800">
            <span
                aria-hidden="true"
                className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-arwen-600 text-xs font-bold leading-none text-white"
            >
                ·
            </span>
            <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                    <NoteTypeChip type={chipType} />
                    {replaces && (
                        <span className="text-xs text-gray-700">
                            Replaces <strong className="font-semibold text-gray-900">{replaces}</strong>
                        </span>
                    )}
                </div>
                <p className="mt-1.5">{children}</p>
                {reachableFrom && (
                    <p className="mt-1 text-xs text-gray-600">Also reachable from {reachableFrom}.</p>
                )}
            </div>
        </aside>
    )
}

// ---------------------------------------------------------------------------
// Inline pin — a numbered arwen dot anchored next to a specific control. Hover
// or tap to reveal the note.
// ---------------------------------------------------------------------------

interface DesignNoteProps {
    n: number
    type?: NoteType
    children: ReactNode
}

export function DesignNote({ n, type = 'rationale', children }: DesignNoteProps) {
    const [visible] = useDesignNotesVisible()
    const [open, setOpen] = useState(false)
    if (!visible) return null

    return (
        <span className="relative inline-flex align-middle">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                onMouseEnter={() => setOpen(true)}
                onMouseLeave={() => setOpen(false)}
                onBlur={() => setOpen(false)}
                aria-label={`Design note ${n}`}
                className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-arwen-600 text-[10px] font-semibold leading-none text-white ring-2 ring-white"
            >
                {n}
            </button>
            {open && (
                <span
                    role="tooltip"
                    className="absolute left-1/2 top-6 z-50 w-64 -translate-x-1/2 rounded-md border border-gray-200 bg-white p-3 text-left text-xs font-normal normal-case tracking-normal text-gray-700 shadow-lg"
                >
                    <NoteTypeChip type={type} />
                    <span className="mt-1.5 block leading-relaxed">{children}</span>
                </span>
            )}
        </span>
    )
}
